import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Button, Dropdown, Input, Icon } from 'semantic-ui-react';
import i18next from 'i18next';
import { fetchRestaurantDataFromGoogle, saveRestaurant, editEntityField, uploadImage } from '../../../actions';
import countries from '../../../consts/Countries.json';
import timezones from '../../../consts/Timezones.json';
import { posConfigFactory } from '../../common/DataGrid/filters/helper';

class RestaurantSetupEditor extends Component {

    constructor(props) {
        super(props);

        this.calculateActivationStatus = this.calculateActivationStatus.bind(this);
    }

    handleSubmit = (event) => {
        this.calculateActivationStatus();
        this.props.saveRestaurant(this.props.entity);
        event.preventDefault();
    }

    calculateActivationStatus() {
        const { entity } = this.props;

        if (entity.activatedAt === null && (
            entity.supportQuickPayment ||
            entity.supportTableOrderCode ||
            entity.supportCartOrderTable ||
            entity.supportCartOrderPickup
        )) {
            entity.activatedAt = new Date();
        }

        if (entity.activatedAt &&
            !entity.supportQuickPayment &&
            !entity.supportTableOrderCode &&
            !entity.supportCartOrderTable &&
            !entity.supportCartOrderPickup
        ) {
            entity.activatedAt = null;
        }
    }

    predictChain = (restName) => {
        if (restName) {
            const restNameParts = String(restName).split(' ');
            const chain = this.props.chains.find((chain) => {
                const occurences = restNameParts.filter(part => chain.name.includes(part)).length;
                return (occurences > 0);
            });
            if (chain) {
                this.props.editEntityField('ownerId', chain.id);
            }
        }
    }

    render() {
        const { entity, chains, editEntityField, uploadImage, fetchRestaurantDataFromGoogle, industries } = this.props;

        const rest = entity || {};

        return (
            <Form onSubmit={this.handleSubmit}>
                <Form.Field>
                    <label>{i18next.t('name')}</label>
                    <Input
                        fluid
                        placeholder={i18next.t('name')}
                        value={rest.name || ''}
                        onChange={({ target }) => editEntityField('name', target.value)}
                        required
                        icon={
                            <Icon
                                name='google'
                                inverted
                                circular
                                link
                                onClick={() => fetchRestaurantDataFromGoogle(rest.name)}
                            />
                        }
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('address')}</label>
                    <input
                        placeholder={i18next.t('address')}
                        value={rest.address || ''}
                        onChange={({ target }) => editEntityField('address', target.value)}
                        required
                    />
                </Form.Field>

                <Form.Group widths='equal'>
                    <Form.Field>
                        <Input
                            fluid
                            placeholder={'lat'}
                            required
                            value={rest.lat || ''}
                            onChange={({ target }) => editEntityField('lat', target.value)}
                            onBlur={() => this.props.editEntityField('lat', parseFloat(rest.lat))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Input
                            fluid
                            placeholder={'lng'}
                            required
                            value={rest.lng || ''}
                            onChange={({ target }) => editEntityField('lng', target.value)}
                            onBlur={() => this.props.editEntityField('lng', parseFloat(rest.lng))}
                        />
                    </Form.Field>
                </Form.Group>

                <Form.Field>
                    <label>{i18next.t('region')}</label>
                    <Dropdown
                        required
                        fluid
                        search
                        selection
                        options={countries.map(country => ({ value: country.alpha_2, text: country.name }))}
                        value={rest.countryCode || ''}
                        onChange={(e, { value }) => {
                            editEntityField('countryCode', value);
                            editEntityField('currencyCode', countries.find(c => c.alpha_2 === value).currency_code);
                            editEntityField('timezone', timezones.find(t => t.country_code === value).timezones[0]);
                        }}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('timezone')}</label>
                    <Dropdown
                        required
                        fluid
                        search
                        selection
                        options={timezones.reduce((final, tz) => final.concat(tz.timezones.map((value, index) => ({ key: tz.name + value + index, value, text: `${tz.name} - ${value}` }))), [])}
                        value={rest.timezone || ''}
                        onChange={(e, { value }) => editEntityField('timezone', value)}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('currency')}</label>
                    <Dropdown
                        required
                        fluid
                        search
                        selection
                        options={countries.map((country, index) => ({ key: index, value: country.currency_code, text: `${country.currency_name} - ${country.currency_code}` }))}
                        value={rest.currencyCode || ''}
                        onChange={(e, { value }) => editEntityField('currencyCode', value)}
                    />
                </Form.Field>

                {rest.taxesSetup &&
                    <Fragment>
                        <Form.Group widths='equal'>
                            <Form.Field>
                                <label>{i18next.t('taxes.type')}</label>
                                <Dropdown
                                    required
                                    fluid
                                    selection
                                    options={['vat', 'salesTax'].map(value => ({ value, text: i18next.t(`taxes.${value}`) }))}
                                    value={rest.taxesSetup.type}
                                    onChange={(e, { value }) => {
                                        editEntityField('taxesSetup.type', value);
                                        editEntityField('taxesSetup.inclusive', value === 'vat');
                                    }}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>{i18next.t(`taxes.${rest.taxesSetup.type}`)}</label>
                                <Input
                                    required
                                    fluid
                                    label="%"
                                    labelPosition='right'
                                    max="100"
                                    type="number"
                                    step="any"
                                    value={Number((rest.taxesSetup.rate * 100).toFixed(3))}
                                    onChange={({ target }) => {                                        
                                        const rate = Number((target.value / 100).toFixed(5));
                                        editEntityField('taxesSetup.rate', rate);
                                        if (rest.taxesSetup.type === 'vat') {
                                            editEntityField('vatRate', rate);
                                        } else {
                                            editEntityField('vatRate', 0);
                                        }
                                    }}
                                />
                            </Form.Field>
                        </Form.Group>
                        <Form.Field>
                            <Form.Checkbox
                                label={i18next.t('taxes.inclusive')}
                                checked={rest.taxesSetup.inclusive}
                                onChange={(e, { checked }) => editEntityField('taxesSetup.inclusive', checked)}
                            />
                        </Form.Field>
                    </Fragment>
                }

                <Form.Field>
                    <label>{i18next.t('industry')}</label>
                    <Dropdown
                        required
                        fluid
                        selection
                        options={industries.map(value => ({ value, text: value }))}
                        value={rest.industry}
                        onChange={(e, { value }) => editEntityField('industry', value)}
                    />
                </Form.Field>

                <Form.Field required>
                    <label>{i18next.t('chain.name')}</label>
                    <Dropdown
                        placeholder={i18next.t('chain.name')}
                        required
                        fluid
                        search
                        selection
                        options={[{ value: null, text: '' }].concat(chains.map((chain) => ({ value: chain.id, text: chain.name })))}
                        value={rest.ownerId || ''}
                        onChange={(e, { value }) => editEntityField('ownerId', value)}
                    />
                </Form.Field>

                <Form.Field required>
                    <label>{i18next.t('posSupplier')}</label>
                    <Dropdown
                        placeholder={i18next.t('posSupplier')}
                        required
                        fluid
                        selection
                        options={this.props.posSuppliers.map((pos) => ({ value: pos.id, text: pos.name }))}
                        value={Number(rest.posSupplierId)}
                        onChange={(e, { value }) => {
                            const posConfig = posConfigFactory(value, this.props.posSuppliers);

                            this.props.editEntityField('posConfig', posConfig);
                            this.props.editEntityField('posSupplierId', value);
                        }}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('registrationNumber')}</label>
                    <input
                        placeholder={i18next.t('registrationNumber')}
                        value={rest.registrationNumber || ''}
                        onChange={({ target }) => editEntityField('registrationNumber', target.value)}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('telephone')}</label>
                    <Input
                        fluid
                        placeholder={i18next.t('telephone')}
                        value={rest.phone || ''}
                        onChange={({ target }) => editEntityField('phone', target.value)}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('salesId')}</label>
                    <input
                        placeholder={i18next.t('salesId')}
                        value={rest.salesSystemId || ''}
                        onChange={({ target }) => editEntityField('salesSystemId', target.value)}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('tableOrder')}</label>
                    <Form.Group inline>
                        <Form.Checkbox
                            label={i18next.t('tableNumber')}
                            checked={Boolean(rest.supportTableOrderCode)}
                            onChange={(e, { checked }) => editEntityField('supportTableOrderCode', checked)}
                        />
                        <Form.Checkbox
                            label={i18next.t('quickPay')}
                            checked={Boolean(rest.supportQuickPayment)}
                            onChange={(e, { checked }) => editEntityField('supportQuickPayment', checked)}
                        />
                    </Form.Group>
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('cartOrder')}</label>
                    <Form.Group inline>
                        <Form.Checkbox
                            label={i18next.t('sitting')}
                            checked={Boolean(rest.supportCartOrderTable)}
                            onChange={(e, { checked }) => editEntityField('supportCartOrderTable', checked)}
                        />
                        <Form.Checkbox
                            label={i18next.t('takeaway')}
                            checked={Boolean(rest.supportCartOrderPickup)}
                            onChange={(e, { checked }) => editEntityField('supportCartOrderPickup', checked)}
                        />
                    </Form.Group>
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('deliveries')}</label>
                    <Form.Group inline>
                        <Form.Checkbox
                            label={i18next.t('deliveries')}
                            checked={Boolean(rest.supportCartOrderDelivery)}
                            onChange={(e, { checked }) => {
                                editEntityField('supportCartOrderDelivery', checked);
                                editEntityField('supportCompanyDelivery', checked);
                            }}
                        />
                        <Form.Checkbox
                            label={i18next.t('companyDelivery')}
                            checked={Boolean(rest.supportCompanyDelivery)}
                            onChange={(e, { checked }) => editEntityField('supportCompanyDelivery', checked)}
                        />
                    </Form.Group>
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('logo')}</label>
                    <Input
                        fluid
                        label={{ basic: true, image: rest.thumbnail }}
                        labelPosition='right'
                        type="file"
                        onChange={({ target }) => uploadImage(target.files[0], 'logo')}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('image')}</label>
                    <Input
                        fluid
                        label={{ basic: true, image: rest.imageUrl }}
                        labelPosition='right'
                        type="file"
                        onChange={({ target }) => uploadImage(target.files[0], 'restCover')}
                    />
                </Form.Field>

                <Button primary type='submit' floated="right" disabled={!rest.posSupplierId || !rest.ownerId}>
                    {i18next.t('save')}
                </Button>
            </Form>
        );
    }
}

RestaurantSetupEditor.propTypes = {
    fetchRestaurantDataFromGoogle: PropTypes.func,
    saveRestaurant: PropTypes.func,
    chains: PropTypes.array,
    industries: PropTypes.array,
    editEntityField: PropTypes.func,
    tags: PropTypes.array,
    posSuppliers: PropTypes.array,
    uploadImage: PropTypes.func,
    entity: PropTypes.object,

};

const mapStateToProps = (state) => {
    const { entity } = state.editor;
    const { posSuppliers, industries } = state.meta;
    const { chains } = state.businessApp;
    const tags = [];

    return { entity, chains, tags, posSuppliers, industries };
};

export default connect(mapStateToProps, {
    fetchRestaurantDataFromGoogle,
    saveRestaurant,
    editEntityField,
    uploadImage,
})(RestaurantSetupEditor);
